import React, { useState, useMemo, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/Button';
import { PageLoader } from '../components/ui/Spinner';
import { useApp } from '../context/AppContext';
import { fmt, fmtDate } from '../utils/format';
import * as api from '../services/api';

/**
 * Inward Stock Pending Queue
 * Implements the exact split-pane layout and look & feel of the mockup:
 *  - Left Pane: Scrollable Pending Inward Queue
 *  - Right Pane: No selection placeholder OR Split view containing:
 *     - Left: Simulated Original Invoice Document (mock PDF visual)
 *     - Right: Extracted Data Editor with form fields, matched client check, and live sheet calculations
 */
function HistoryImageCell({ id, supplier }) {
  const [imgUrl, setImgUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    let active = true;
    api.getInwardImage(id)
      .then(res => {
        if (active && res?.url) {
          setImgUrl(res.url);
        }
      })
      .catch(err => {
        console.error('Failed to load cell signed image:', err);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [id]);

  if (loading) {
    return <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Loading…</span>;
  }

  if (!imgUrl) {
    return <span style={{ fontSize: 11, color: 'var(--accent-red)' }}>⚠️ Error</span>;
  }

  return (
    <>
      <div 
        onClick={() => setZoomed(true)}
        style={{
          width: 44,
          height: 44,
          borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--border-color)',
          overflow: 'hidden',
          cursor: 'zoom-in',
          backgroundColor: '#0f172a',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'transform 0.2s',
        }}
        className="thumbnail-preview"
      >
        <img 
          src={imgUrl} 
          alt="Inward Attachment" 
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </div>

      {zoomed && (
        <div 
          className="image-popup-overlay" 
          onClick={() => setZoomed(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', zIndex: 10000, cursor: 'zoom-out',
            animation: 'fadeIn 0.2s ease'
          }}
        >
          <div style={{ position: 'relative', maxWidth: '90%', maxHeight: '90%' }} onClick={e => e.stopPropagation()}>
            <button 
              onClick={() => setZoomed(false)}
              style={{
                position: 'absolute', top: -40, right: 0, background: 'none', border: 'none',
                color: 'white', fontSize: 24, cursor: 'pointer'
              }}
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
            <img 
              src={imgUrl} 
              alt={`Inward Invoice Scan from ${supplier}`} 
              style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 8, boxShadow: '0 8px 30px rgba(0,0,0,0.5)', cursor: 'default' }} 
            />
          </div>
        </div>
      )}
    </>
  );
}

export default function PendingQueue() {
  const { state, cl, fetchInward, doConfirmInward, doRejectInward, doUpdateInward, doCreateClientFromInward, doDeleteInward } = useApp();

  const [selId, setSelId]               = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [imageUrl, setImageUrl]           = useState(null);
  const [imageLoading, setImageLoading]   = useState(false);

  // Client creation modal state
  const [showCreateClientModal, setShowCreateClientModal] = useState(false);
  const [createClientForm, setCreateClientForm] = useState(null);

  // Editing State
  const [editData, setEditData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');

  // Load selected invoice attachment signed URL
  useEffect(() => {
    if (!selId) {
      setImageUrl(null);
      return;
    }
    let active = true;
    setImageLoading(true);
    setImageUrl(null);
    api.getInwardImage(selId)
      .then(res => {
        if (active && res?.url) {
          setImageUrl(res.url);
        }
      })
      .catch(err => {
        console.error('Failed to load signed image URL:', err);
      })
      .finally(() => {
        if (active) setImageLoading(false);
      });
    return () => { active = false; };
  }, [selId]);

  // Filter pending inward stock invoices
  const pendingInward = useMemo(() => {
    return state.inward.filter(x => x.status === 'pending');
  }, [state.inward]);

  // Filter history inward stock invoices
  const historyInward = useMemo(() => {
    return state.inward
      .filter(x => x.status !== 'pending')
      .sort((a, b) => (b.recv_at || b.confirmed_at || '').localeCompare(a.recv_at || a.confirmed_at || ''));
  }, [state.inward]);

  const selectedInward = useMemo(() => {
    return pendingInward.find(x => x.id === selId) || null;
  }, [pendingInward, selId]);

  // Track the previously selected ID so we can detect actual selection changes
  const prevSelIdRef = React.useRef(null);

  // Sync editData when selection changes (selId)
  useEffect(() => {
    const isNewSelection = prevSelIdRef.current !== selId;
    prevSelIdRef.current = selId;
    if (selectedInward) {
      if (isNewSelection) {
        // New item selected — reset to fresh data and exit edit mode
        setEditData(JSON.parse(JSON.stringify(selectedInward)));
        setIsEditing(false);
      }
      // If same item, do nothing — preserve in-progress edits
    } else {
      setEditData(null);
      setIsEditing(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selId, selectedInward]);



  // Client matching logic
  const matchedClient = useMemo(() => {
    if (!editData) return null;
    const gstin = editData.data?.buyer?.gstin || '';
    return state.clients.find(c => c.gstin === gstin.trim()) || null;
  }, [editData, state.clients]);

  // Sheet calculation rules
  const getSheetsPerUnit = (desc) => {
    const d = (desc || '').toUpperCase();
    if (d.includes('PAPER') || d.includes('MAPLITHO') || d.includes('ART') || d.includes('COATED') || d.includes('RIM')) {
      return 500; // Paper Rim
    }
    return 144; // Board Gross
  };

  // Handler for line item edits
  const handleItemChange = (idx, field, val) => {
    setEditData(prev => {
      const next = { ...prev };
      next.data = { ...next.data };
      next.data.items = [...next.data.items];
      next.data.items[idx] = { ...next.data.items[idx], [field]: val };
      return next;
    });
  };

  // Actions
  async function handleConfirm() {
    if (!selectedInward) return;
    setActionLoading(true);
    try {
      await doConfirmInward(selectedInward.id);
      setSelId(null);
    } catch (e) {
      if (e.status === 404 || (e.message && e.message.toLowerCase().includes('not found') && e.message.toLowerCase().includes('client'))) {
        setCreateClientForm({
          name: editData.data?.buyer?.name || '',
          gstin: editData.data?.buyer?.gstin || '',
          address: editData.data?.buyer?.address || '',
          phone: '',
        });
        setShowCreateClientModal(true);
      }
    }
    finally { setActionLoading(false); }
  }

  async function handleCreateClientSubmit(e) {
    e.preventDefault();
    setActionLoading(true);
    try {
      await doCreateClientFromInward(selectedInward.id, createClientForm);
      setShowCreateClientModal(false);
      // Auto-retry confirm after client creation
      await doConfirmInward(selectedInward.id);
      setSelId(null);
    } catch (err) {
      // error handled in context
    } finally {
      setActionLoading(false);
    }
  }

  async function handleReject() {
    if (!selectedInward) return;
    setActionLoading(true);
    try {
      await doRejectInward(selectedInward.id);
      setSelId(null);
    } catch (_) {}
    finally { setActionLoading(false); }
  }

  async function handleSaveEdits() {
    if (!editData) return;
    setActionLoading(true);
    try {
      const updates = {
        supplier_name: editData.supplier,
        invoice_number: editData.inv_no,
        invoice_date: editData.inv_date,
        total_amount: editData.total_amt,
        extracted_data: editData.data,
      };
      if (matchedClient) {
        updates.client_id = matchedClient.id;
      }
      await doUpdateInward(selectedInward.id, updates);
      setIsEditing(false);
    } catch (_) {
    } finally {
      setActionLoading(false);
    }
  }

  async function handleDeleteInward(id) {
    if (!window.confirm('Delete this inward record? This cannot be undone.')) return;
    try {
      await doDeleteInward(id);
      if (selId === id) setSelId(null);
    } catch (_) {}
  }

  const isFirstLoad = state.loading.inward && !state.inward.length;
  if (isFirstLoad) {
    return <Layout><PageLoader label="Loading inward queue…" /></Layout>;
  }

  return (
    <Layout
      actions={
        <Button variant="outline" size="sm" onClick={fetchInward}>
          ↺ Refresh Queue
        </Button>
      }
    >
      <div className="tabs-container">
        <div className="tabs-header">
          <button
            className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => { setActiveTab('pending'); setSelId(null); }}
          >
            Pending Inward Queue
          </button>
          <button
            className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => { setActiveTab('history'); setSelId(null); }}
          >
            Finalized History Log
          </button>
        </div>

        <div className="tabs-content" style={{ marginTop: 16 }}>
          {activeTab === 'pending' && (
            <div className="split-layout">
              {/* ── Left Pane: Scrollable Queue ── */}
              <div className="pane-left glass-card">
                <div className="card-header">
                  <h3>Pending Inward Queue</h3>
                </div>
                <div className="scrollable-list">
                  {pendingInward.length === 0 ? (
                    <div className="placeholder-text" style={{ padding: 24, textAlign: 'center' }}>
                      No pending inward stock reviews.
                    </div>
                  ) : (
                    pendingInward.map(item => {
                      const isSelected = item.id === selId;
                      return (
                        <div
                          key={item.id}
                          className={`list-item ${isSelected ? 'selected' : ''}`}
                          onClick={() => setSelId(item.id)}
                        >
                          <h4>{item.supplier}</h4>
                          <p>Inv: #{item.inv_no} | Date: {item.inv_date}</p>
                          <div className="list-item-meta" style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                            <div>
                              <span className="badge badge-warning">Review Needed</span>
                              <span className="date" style={{ marginLeft: 6 }}>{fmtDate(item.recv_at)}</span>
                            </div>
                            <button 
                              onClick={e => { e.stopPropagation(); handleDeleteInward(item.id); }}
                              style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}
                              title="Delete record"
                            >
                              <i className="fa-solid fa-trash-can"></i>
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* ── Right Pane: Split Details Panel ── */}
              <div className="pane-right">
                {!selectedInward || !editData ? (
                  <div className="no-selection-placeholder">
                    <i className="fa-solid fa-file-invoice" style={{ fontSize: 32, marginBottom: 12, opacity: 0.5 }}></i>
                    <p>Select a pending inward invoice from the list to review and confirm stock.</p>
                  </div>
                ) : (
                  <div className="split-view-container" style={{ height: '100%', overflow: 'hidden' }}>
                    
                    {/* Image viewer / simulated invoice document visual (Left) */}
                    <div className="image-viewer glass-card" style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                      <div className="image-header" style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span><i className="fa-solid fa-image"></i> Original Invoice Attachment</span>
                        {imageUrl && (
                          <a href={imageUrl} target="_blank" rel="noopener noreferrer" className="btn btn-icon-only" title="Open raw image" style={{ color: 'var(--text-secondary)' }}>
                            <i className="fa-solid fa-expand"></i>
                          </a>
                        )}
                      </div>
                      <div className="simulated-image-container" style={{ flexGrow: 1, padding: 12, overflowY: 'auto', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', background: 'rgba(0,0,0,0.2)' }}>
                        {imageLoading ? (
                          <div className="placeholder-text" style={{ alignSelf: 'center' }}>Signing attachment credentials…</div>
                        ) : imageUrl ? (
                          <img 
                            src={imageUrl} 
                            alt="Original Supplier Invoice Attachment" 
                            style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }} 
                          />
                        ) : (
                          <div className="placeholder-text" style={{ alignSelf: 'center', color: 'var(--accent-red)' }}>⚠️ Failed to load original invoice image</div>
                        )}
                      </div>
                    </div>

                    {/* Extracted Data Editor (Right) */}
                    <div className="editor-panel glass-card" style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                      <div className="card-header" style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <button
                            onClick={() => setSelId(null)}
                            className="btn btn-icon-only"
                            title="Close panel"
                            style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: '4px 8px' }}
                          >
                            <i className="fa-solid fa-arrow-left-long"></i>
                          </button>
                          <h3 style={{ whiteSpace: 'nowrap' }}>Extracted Data Editor</h3>
                          <span className="confidence-badge positive" style={{ fontSize: 10, background: 'rgba(0, 230, 118, 0.1)', color: 'var(--accent-green)', padding: '2px 8px', borderRadius: 12, whiteSpace: 'nowrap' }}>Gemini Extracted</span>
                        </div>
                        <button 
                          onClick={() => setIsEditing(!isEditing)} 
                          className="btn btn-icon-only" 
                          title={isEditing ? "Cancel editing" : "Edit fields"}
                          style={{ color: isEditing ? 'var(--accent-red)' : 'var(--accent-cyan)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}
                        >
                          <i className={isEditing ? "fa-solid fa-xmark" : "fa-solid fa-pen-to-square"}></i>
                        </button>
                      </div>
                      
                      <form className="dashboard-form" onSubmit={e => e.preventDefault()} style={{ flexGrow: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Supplier Name</label>
                            <input 
                              type="text" 
                              value={editData.supplier || ''} 
                              onChange={(e) => setEditData(prev => ({ ...prev, supplier: e.target.value }))}
                              readOnly={!isEditing} 
                              className={isEditing ? 'editable-input' : ''}
                            />
                          </div>
                        </div>
                        <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                          <div className="form-group">
                            <label>Invoice Number</label>
                            <input 
                              type="text" 
                              value={editData.inv_no || ''} 
                              onChange={(e) => setEditData(prev => ({ ...prev, inv_no: e.target.value }))}
                              readOnly={!isEditing} 
                              className={isEditing ? 'editable-input' : ''}
                            />
                          </div>
                          <div className="form-group">
                            <label>Invoice Date</label>
                            <input 
                              type={isEditing ? "date" : "text"} 
                              value={editData.inv_date || ''} 
                              onChange={(e) => setEditData(prev => ({ ...prev, inv_date: e.target.value }))}
                              readOnly={!isEditing} 
                              className={isEditing ? 'editable-input' : ''}
                            />
                          </div>
                        </div>
                        <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                          <div className="form-group">
                            <label>Buyer Name</label>
                            <input 
                              type="text" 
                              value={editData.data?.buyer?.name || ''} 
                              onChange={(e) => setEditData(prev => {
                                const next = { ...prev };
                                next.data = { ...next.data };
                                next.data.buyer = { ...next.data.buyer, name: e.target.value };
                                return next;
                              })}
                              readOnly={!isEditing} 
                              className={isEditing ? 'editable-input' : ''}
                            />
                          </div>
                          <div className="form-group">
                            <label>Buyer GSTIN (Client Auto-Match Key)</label>
                            <input 
                              type="text" 
                              value={editData.data?.buyer?.gstin || ''} 
                              onChange={(e) => setEditData(prev => {
                                const next = { ...prev };
                                next.data = { ...next.data };
                                next.data.buyer = { ...next.data.buyer, gstin: e.target.value };
                                return next;
                              })}
                              readOnly={!isEditing} 
                              className={isEditing ? 'editable-input' : ''}
                            />
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Buyer Address</label>
                            <input 
                              type="text" 
                              value={editData.data?.buyer?.address || ''} 
                              onChange={(e) => setEditData(prev => {
                                const next = { ...prev };
                                next.data = { ...next.data };
                                next.data.buyer = { ...next.data.buyer, address: e.target.value };
                                return next;
                              })}
                              readOnly={!isEditing} 
                              className={isEditing ? 'editable-input' : ''}
                            />
                          </div>
                        </div>

                        <div className="form-row" style={{ border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: 12, background: 'rgba(255,255,255,0.01)' }}>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 8, textTransform: 'uppercase', fontWeight: 600 }}>Matched Client Target</span>
                          {matchedClient ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--accent-green)', fontSize: 13 }}>
                              <i className="fa-solid fa-circle-check"></i>
                              <span>Matched: <strong>{matchedClient.name}</strong></span>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--accent-red)', fontSize: 13 }}>
                              <i className="fa-solid fa-triangle-exclamation"></i>
                              <span>No matching client found for this GSTIN</span>
                            </div>
                          )}
                        </div>

                        <div className="extracted-items-container" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          <label style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600 }}>Extracted Material Items ({editData.data?.items?.length || 0})</label>
                          <div className="table-wrapper">
                            <table className="dashboard-table compact">
                              <thead>
                                <tr>
                                  <th>Description</th>
                                  <th className="align-right">Qty (kg)</th>
                                  <th className="align-right">Rate</th>
                                  <th className="align-right">Amount</th>
                                  <th className="align-right">GSM</th>
                                  <th className="align-right">Sheets</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(editData.data?.items || []).map((item, idx) => {
                                  const kg = Number(item.qty_kg || 0);
                                  const sheetsPerUnit = getSheetsPerUnit(item.description);
                                  const computedSheets = Math.round(kg * (sheetsPerUnit / 25)); // rule-of-thumb: 25kg packaging weight baseline or dynamic metric
                                  
                                  return (
                                    <tr key={idx}>
                                      <td>
                                        {isEditing ? (
                                          <input 
                                            type="text" 
                                            style={{ width: '100%', padding: '2px 4px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-color)', borderRadius: 4, color: '#fff' }}
                                            value={item.description || ''} 
                                            onChange={(e) => handleItemChange(idx, 'description', e.target.value)} 
                                          />
                                        ) : (
                                          item.description
                                        )}
                                      </td>
                                      <td className="align-right">
                                        {isEditing ? (
                                          <input 
                                            type="number" 
                                            style={{ width: 60, textAlign: 'right', padding: '2px 4px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-color)', borderRadius: 4, color: '#fff' }}
                                            value={item.qty_kg || 0} 
                                            onChange={(e) => handleItemChange(idx, 'qty_kg', parseFloat(e.target.value) || 0)} 
                                          />
                                        ) : (
                                          kg.toLocaleString()
                                        )}
                                      </td>
                                      <td className="align-right">
                                        {isEditing ? (
                                          <input 
                                            type="number" 
                                            style={{ width: 60, textAlign: 'right', padding: '2px 4px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-color)', borderRadius: 4, color: '#fff' }}
                                            value={item.rate || 0} 
                                            onChange={(e) => handleItemChange(idx, 'rate', parseFloat(e.target.value) || 0)} 
                                          />
                                        ) : (
                                          fmt(item.rate || 0)
                                        )}
                                      </td>
                                      <td className="align-right">{fmt((item.amount || (kg * (item.rate || 0))))}</td>
                                      <td className="align-right">
                                        {isEditing ? (
                                          <input 
                                            type="number" 
                                            style={{ width: 50, textAlign: 'right', padding: '2px 4px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-color)', borderRadius: 4, color: '#fff' }}
                                            value={item.gsm || 0} 
                                            onChange={(e) => handleItemChange(idx, 'gsm', parseInt(e.target.value) || 0)} 
                                          />
                                        ) : (
                                          item.gsm || '—'
                                        )}
                                      </td>
                                      <td className="align-right"><strong>{computedSheets.toLocaleString()}</strong></td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        <div className="summary-amounts-grid" style={{ marginTop: 8, padding: '12px 16px', background: 'rgba(255,255,255,0.01)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>
                            <span>Subtotal</span>
                            <span>{fmt(editData.total_amt - ((editData.data?.cgst || 0) + (editData.data?.sgst || 0) + (editData.data?.loading || 0)))}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>
                            <span>CGST 9%</span>
                            {isEditing ? (
                              <input 
                                type="number" 
                                style={{ width: 80, textAlign: 'right', padding: '2px 6px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-color)', borderRadius: 4, color: '#fff' }}
                                value={editData.data?.cgst || 0}
                                onChange={(e) => setEditData(prev => {
                                  const next = { ...prev };
                                  next.data = { ...next.data, cgst: parseFloat(e.target.value) || 0 };
                                  return next;
                                })}
                              />
                            ) : (
                              <span>{fmt(editData.data?.cgst || 0)}</span>
                            )}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>
                            <span>SGST 9%</span>
                            {isEditing ? (
                              <input 
                                type="number" 
                                style={{ width: 80, textAlign: 'right', padding: '2px 6px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-color)', borderRadius: 4, color: '#fff' }}
                                value={editData.data?.sgst || 0}
                                onChange={(e) => setEditData(prev => {
                                  const next = { ...prev };
                                  next.data = { ...next.data, sgst: parseFloat(e.target.value) || 0 };
                                  return next;
                                })}
                              />
                            ) : (
                              <span>{fmt(editData.data?.sgst || 0)}</span>
                            )}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
                            <span>Loading Charges</span>
                            {isEditing ? (
                              <input 
                                type="number" 
                                style={{ width: 80, textAlign: 'right', padding: '2px 6px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-color)', borderRadius: 4, color: '#fff' }}
                                value={editData.data?.loading || 0}
                                onChange={(e) => setEditData(prev => {
                                  const next = { ...prev };
                                  next.data = { ...next.data, loading: parseFloat(e.target.value) || 0 };
                                  return next;
                                })}
                              />
                            ) : (
                              <span>{fmt(editData.data?.loading || 0)}</span>
                            )}
                          </div>
                          <div className="total-highlight" style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 'bold', color: 'var(--text-primary)', borderTop: '1px solid var(--border-color)', paddingTop: 8 }}>
                            <span>Total Invoice Value</span>
                            {isEditing ? (
                              <input 
                                type="number" 
                                style={{ width: 100, textAlign: 'right', padding: '4px 8px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-color)', borderRadius: 4, color: '#fff', fontWeight: 'bold' }}
                                value={editData.total_amt || 0}
                                onChange={(e) => setEditData(prev => ({ ...prev, total_amt: parseFloat(e.target.value) || 0 }))}
                              />
                            ) : (
                              <span>{fmt(editData.total_amt)}</span>
                            )}
                          </div>
                        </div>

                        <div className="editor-actions" style={{ marginTop: 'auto', paddingTop: 16, display: 'flex', gap: 12 }}>
                          {isEditing ? (
                            <>
                              <Button variant="danger" disabled={actionLoading} onClick={() => setIsEditing(false)} style={{ flexGrow: 1 }}>
                                Cancel
                              </Button>
                              <Button variant="primary" disabled={actionLoading} onClick={handleSaveEdits} style={{ flexGrow: 1 }}>
                                {actionLoading ? 'Saving…' : 'Save Changes'}
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button variant="danger" disabled={actionLoading} onClick={handleReject} style={{ flexGrow: 1 }}>
                                {actionLoading ? '…' : 'Reject'}
                              </Button>
                              <Button
                                variant="primary"
                                disabled={actionLoading}
                                onClick={handleConfirm}
                                style={{ flexGrow: 1.5 }}
                              >
                                {actionLoading ? 'Ingesting…' : 'Confirm & Ingest Stock'}
                              </Button>
                            </>
                          )}
                        </div>
                        <div style={{ height: 24, flexShrink: 0 }}></div>
                      </form>
                    </div>

                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="history-table-container glass-card" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="card-header" style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: 16, marginBottom: 8 }}>
                <h3 style={{ margin: 0, fontSize: 16 }}>Finalized Inward History</h3>
              </div>

              {historyInward.length === 0 ? (
                <div className="placeholder-text" style={{ padding: 48, textAlign: 'center' }}>
                  No finalized inward history records found.
                </div>
              ) : (
                <div className="table-wrapper">
                  <table className="dashboard-table">
                    <thead>
                      <tr>
                        <th>Invoice Number</th>
                        <th>Invoice Scan</th>
                        <th>Supplier Name</th>
                        <th>Invoice Date</th>
                        <th>Ingestion Date</th>
                        <th>Matched Client</th>
                        <th>Total Value</th>
                        <th className="align-right">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyInward.map(item => {
                        const client = cl(item.client_id);
                        const isConfirmed = item.status === 'confirmed';
                        const statusBadge = isConfirmed ? 'badge-success' : 'badge-danger';

                        return (
                          <tr key={item.id}>
                            <td><strong style={{ fontFamily: 'var(--font-mono)' }}>#{item.inv_no}</strong></td>
                            <td>
                              <HistoryImageCell id={item.id} supplier={item.supplier} />
                            </td>
                            <td>{item.supplier}</td>
                            <td>{item.inv_date}</td>
                            <td>{fmtDate(item.recv_at || item.confirmed_at)}</td>
                            <td>{client ? client.name : '—'}</td>
                            <td><strong>{fmt(item.total_amt)}</strong></td>
                            <td className="align-right">
                              <span className={`badge ${statusBadge}`} style={{ textTransform: 'uppercase', fontSize: 10 }}>
                                {item.status}
                              </span>
                              {item.status === 'rejected' && (
                                <button 
                                  onClick={e => { e.stopPropagation(); handleDeleteInward(item.id); }}
                                  style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', marginLeft: 8 }}
                                  title="Delete record"
                                >
                                  <i className="fa-solid fa-trash-can"></i>
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showCreateClientModal && (
        <div className="modal-overlay active">
          <div className="modal-card">
            <div className="modal-header">
              <h3>
                <i className="fa-solid fa-user-plus"></i> New Client Registration
              </h3>
              <button className="btn-close-modal" onClick={() => setShowCreateClientModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleCreateClientSubmit}>
              <div className="modal-body">
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                  Client not found in system. Register them now to proceed with stock ingestion.
                </p>
                <div className="form-group">
                  <label>Company / Client Name *</label>
                  <input
                    type="text"
                    required
                    value={createClientForm?.name || ''}
                    onChange={e => setCreateClientForm(f => ({ ...f, name: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Client GSTIN *</label>
                  <input
                    type="text"
                    required
                    maxLength={15}
                    value={createClientForm?.gstin || ''}
                    onChange={e => setCreateClientForm(f => ({ ...f, gstin: e.target.value.toUpperCase() }))}
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>
                <div className="form-group">
                  <label>Contact Phone</label>
                  <input
                    type="text"
                    value={createClientForm?.phone || ''}
                    onChange={e => setCreateClientForm(f => ({ ...f, phone: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Billing Address</label>
                  <textarea
                    rows={3}
                    value={createClientForm?.address || ''}
                    onChange={e => setCreateClientForm(f => ({ ...f, address: e.target.value }))}
                    style={{
                      background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-color)',
                      borderRadius: 'var(--radius-md)', padding: '10px 14px', fontSize: 13, color: 'var(--text-primary)',
                      resize: 'vertical', width: '100%', outline: 'none'
                    }}
                  />
                </div>
              </div>
              <div className="modal-footer" style={{ marginTop: 20 }}>
                <Button type="button" variant="outline" onClick={() => setShowCreateClientModal(false)}>Cancel</Button>
                <Button type="submit" variant="primary" disabled={actionLoading}>
                  {actionLoading ? 'Registering & Confirming…' : 'Register & Confirm'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
